import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Order = Tables<'orders'> & { organizations: { name: string } | null }

const STATUSES = ['all', 'received', 'validated', 'acknowledged', 'in_production', 'ready_to_ship', 'shipped', 'delivered', 'cancelled'] as const

export function AdminOrdersListPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  useEffect(() => {
    async function load() {
      let query = supabase.from('orders').select('*, organizations(name)')
        .order('created_at', { ascending: false })
      if (statusFilter !== 'all') query = query.eq('status', statusFilter as Tables<'orders'>['status'])
      const { data } = await query
      setOrders((data as unknown as Order[]) || [])
      setLoading(false)
    }
    setLoading(true)
    load()
  }, [statusFilter])

  return (
    <div>
      <PageHeader title="Orders" description="Manage all orders across distributors" />

      <div className={css.tabs} style={{ marginBottom: 'var(--space-6)' }}>
        {STATUSES.map(s => (
          <button key={s} className={`${css.tab} ${statusFilter === s ? css.tabActive : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p> : orders.length === 0 ? (
        <EmptyState title="No orders found" description="Orders will appear here when distributors convert quotes" />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Organization</th>
                <th>Status</th>
                <th>Total</th>
                <th>PO Number</th>
                <th>Promised Delivery</th>
                <th>Confidence</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className={css.tableClickableRow} onClick={() => navigate(`/admin/orders/${o.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{o.reference_code}</code></td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{o.organizations?.name || '—'}</td>
                  <td><StatusBadge label={o.status} /></td>
                  <td style={{ fontWeight: 500 }}>£{Number(o.total).toLocaleString()}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{o.po_number || '—'}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>
                    {o.promised_delivery ? new Date(o.promised_delivery).toLocaleDateString() : 'Pending'}
                  </td>
                  <td>
                    {o.delivery_confidence ? (
                      <StatusBadge
                        label={o.delivery_confidence}
                        variant={o.delivery_confidence === 'high' ? 'success' : o.delivery_confidence === 'medium' ? 'warning' : 'error'}
                      />
                    ) : '—'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
